import { useGet } from "@/lib/api";
import { Scale } from "lucide-react";

const C = { UP: "#10B981", DOWN: "#EF4444", FLAT: "#9CA3AF" };
const dir = (v) => (v === "BUY" || v === "LONG" || v === "UP" ? "UP" : v === "SELL" || v === "SHORT" || v === "DOWN" ? "DOWN" : "FLAT");

function Cell({ label, value, sub, d, testId }) {
  const color = C[d];
  return (
    <div data-testid={testId} className="flex-1 min-w-[120px] rounded-xl px-3 py-2" style={{ background: `${color}14`, border: `1px solid ${color}44` }}>
      <div className="text-[9px] uppercase tracking-wider text-[#6B7280] font-mono-num">{label}</div>
      <div className="font-display font-extrabold text-base leading-tight mt-0.5" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-[#9CA3AF] font-mono-num leading-snug mt-0.5">{sub}</div>}
    </div>
  );
}

export function AgreementStrip({ signal, refreshKey }) {
  const { data: b } = useGet("/market-drivers/dxy-gold/bias", refreshKey);
  const { data: t } = useGet("/technical?interval=15m", refreshKey);
  const { data: ag } = useGet("/agreement", refreshKey);
  const mtf = t?.mtf;
  const macroD = dir(signal?.action);
  const posD = dir(b?.bias);
  const mtfD = mtf?.aligned === "UP" || mtf?.aligned === "DOWN" ? mtf.aligned : "FLAT";
  const dirs = [macroD, posD, mtfD];
  const agree = dirs.every((x) => x === "UP") ? "UP" : dirs.every((x) => x === "DOWN") ? "DOWN" : null;
  const verdict = agree
    ? `All three agree → high-conviction ${agree === "UP" ? "LONG" : "SHORT"}${ag?.since ? ` · since ${new Date(ag.since).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""} · you were alerted when this lined up`
    : mtfD === "FLAT"
      ? "Macro and 4H position are early reads — timeframes not aligned, no scalps yet · you'll be alerted the moment all three line up"
      : "Views disagree — wait for the 4H crossing and MTF ladder to confirm the macro lean · you'll be alerted the moment all three line up";
  return (
    <section data-testid="agreement-strip" className="glass rounded-2xl px-4 py-3 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Scale size={14} className="text-[#FB923C]" />
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280] font-mono-num">Agreement check · Macro vs 4H position vs MTF</span>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Cell testId="agree-macro" label="Macro forecast" d={macroD}
          value={signal ? `${signal.action} ${signal.confidence}%` : "—"} sub="Data surprises + DXY/yields momentum · days–weeks" />
        <Cell testId="agree-position" label="4H position" d={posD}
          value={b?.bias ? (posD === "UP" ? "BUY" : "SELL") : b?.warming_up ? "Warming up" : "—"}
          sub={b?.since ? `Gold vs DXY crossing · since ${b.since.slice(0, 10)}` : "Gold vs DXY 4H crossing"} />
        <Cell testId="agree-mtf" label="M5/M15 gate" d={mtfD}
          value={mtfD === "FLAT" ? "BLOCKED" : mtfD === "UP" ? "BUY ONLY" : "SELL ONLY"}
          sub={mtf?.ladder || (t?.warming_up ? "Warming up" : "W · D · H4 · H1 trend ladder")} />
      </div>
      <p data-testid="agree-verdict" className="text-[11px] text-[#D1D5DB] leading-snug">{verdict}</p>
    </section>
  );
}
